'use client'

import {
  Toaster as ChakraToaster,
  createToaster,
  Portal,
  Spinner,
  Stack,
  Toast,
} from '@chakra-ui/react'
import React from 'react'

/**
 * The only toast types @zag-js/toast can prioritize. Its own `Options['type']`
 * widens to `string`, so a token it doesn't know (a Chakra color such as
 * `red.solid`) type-checks but makes its internal `priorities[type]` lookup
 * return `undefined`, which throws `TypeError: undefined is not iterable` when
 * the toast is queued. Annotate a computed `type` with this to get a build
 * error instead.
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading'

// Main toaster for most notifications (top-right)
export const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

// Bottom-left toaster for feedback toast
export const bottomLeftToaster = createToaster({
  placement: 'bottom-start',
  pauseOnPageIdle: true,
})

const ToasterContent = ({
  toasterInstance,
}: {
  toasterInstance: typeof toaster
}) => {
  return (
    <ChakraToaster
      toaster={toasterInstance}
      insetInline={{ mdDown: '4' }}
      gap={4}
    >
      {(toast) => {
        // Check if a custom render function is provided in meta
        const customRender = toast.meta?.customRender as
          ((props: { onClose: () => void }) => React.ReactNode) | undefined
        const closable =
          (toast.meta?.closable as boolean | undefined) ?? toast.closable

        // If a custom render function is provided, use it
        if (customRender) {
          return customRender({
            onClose: () => toasterInstance.dismiss(toast.id),
          })
        }

        // Otherwise use the default toast
        return (
          <Toast.Root width={{ md: 'sm' }}>
            {toast.type === 'loading' ? (
              <Spinner size="sm" color="blue.solid" />
            ) : (
              <Toast.Indicator color="white" />
            )}
            <Stack gap="1" flex="1" maxWidth="100%">
              {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
              {toast.description && (
                <Toast.Description>{toast.description}</Toast.Description>
              )}
            </Stack>
            {toast.action && (
              <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
            )}
            {closable && <Toast.CloseTrigger color="white" />}
          </Toast.Root>
        )
      }}
    </ChakraToaster>
  )
}

export const Toaster = () => {
  return (
    <Portal>
      <ToasterContent toasterInstance={toaster} />
      <ToasterContent toasterInstance={bottomLeftToaster} />
    </Portal>
  )
}
